import React, {useState, useEffect} from "react";
import {
    Descriptions,
    Modal,
    Table,
    Typography,
    Button,
    Form,
    Row,
    Col,
    Space,
    Select
} from "antd";
import {motion} from "framer-motion";
import {
    SearchOutlined,
    UndoOutlined,
    DownloadOutlined
} from "@ant-design/icons";
import {useSelector} from "react-redux";
import {useMediaQuery} from "react-responsive";
import apiClient from "../services/apiService";
import {MapContainer, TileLayer, Marker, Popup} from "react-leaflet";
import {COT_ROLE, PROMOTER_ROLE, SECTIONAL_ROLE, useUserRole} from "../utils/userSessionUtils";
import "leaflet/dist/leaflet.css";
import L from "leaflet";

// Fix leaflet default marker icon
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
    iconUrl: require("leaflet/dist/images/marker-icon.png"),
    shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
});

const {Title} = Typography;
const {Option} = Select;

const DirectorySurveyPage = () => {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [form] = Form.useForm();
    const [selectedRecord, setSelectedRecord] = useState(null);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const auth = useSelector((state) => state.auth || {});
    const user = auth.user || {};
    const userRole = useUserRole();
    const {entityId, sections = []} = user;
    const userSections = [...sections]; // Hardcoded section for testing
    const userEntities = Array.isArray(entityId) ? entityId : [entityId];
    const isMobile = useMediaQuery({maxWidth: 820});
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
        total: 0,
    });
    const [selectedEntity, setSelectedEntity] = useState(null);
    const [selectedSection, setSelectedSection] = useState(null);
    const [mapKey, setMapKey] = useState(0);

    useEffect(() => {
        if (isModalVisible && selectedRecord?.latLong) {
            // Force map re-render when modal opens
            setTimeout(() => setMapKey(prev => prev + 1), 100);
        }
    }, [isModalVisible, selectedRecord]);

    const fetchDirectorySurvey = async () => {
        if (!selectedEntity || !selectedSection) {
            Modal.warning({
                title: 'Campos requeridos',
                content: 'Por favor selecciona una entidad y una sección',
            });
            return;
        }

        setLoading(true);
        try {
            const params = new URLSearchParams({
                entityId: selectedEntity,
                sectionId: selectedSection,
            });
            const response = await apiClient.get(`/directory-survey?${params.toString()}`);
            const allData = response.data.data || [];
            setData(allData);
            setPagination((prev) => ({
                ...prev,
                current: 1,
                total: allData.length,
            }));
        } catch (error) {
            console.error("Error fetching directory survey:", error);
            Modal.error({
                title: 'Error',
                content: 'Error al obtener los datos del directorio',
            });
        } finally {
            setLoading(false);
        }
    };

    const handleTableChange = (paginationInfo) => {
        setPagination((prev) => ({
            ...prev,
            current: paginationInfo.current,
            pageSize: paginationInfo.pageSize,
        }));
    };

    const handleSearch = () => {
        fetchDirectorySurvey();
    };

    const handleReset = () => {
        form.resetFields();
        setSelectedEntity(null);
        setSelectedSection(null);
        setData([]);
        setPagination((prev) => ({...prev, current: 1, total: 0}));
    };

    const downloadCSV = () => {
        if (data.length === 0) {
            Modal.warning({
                title: 'Sin datos',
                content: 'No hay datos para descargar',
            });
            return;
        }

        const headers = [
            'Nombre completo',
            // 'Clave de elector',
            // 'CURP',
            'Teléfono',
            'Email',
            'Sección',
            'Entidad',
            'Municipio',
            'Distrito',
            'Dirección INE',
            // 'ID Afiliación',
            'Formación',
            'Comité',
            'Promotor',
            'RC',
            'Participación',
            'Coordenadas'
        ];

        const rows = data.map(record => [
            `${record.name || ''} ${record.firstLastName || ''} ${record.secondLastName || ''}`.trim(),
            // record.electorKey || '',
            // record.curp || '',
            record.phone || '',
            record.email || '',
            record.section || '',
            record.entityId || '',
            record.municipality || '',
            record.district || '',
            record.ineAddress || '',
            // record.affiliationId || '',
            record.formation ? 'Sí' : 'No',
            record.committee ? 'Sí' : 'No',
            record.promoter ? 'Sí' : 'No',
            record.rc ? 'Sí' : 'No',
            record.participationArray ? record.participationArray.join(';') : '',
            record.latLong || ''
        ]);

        const csvContent = [
            headers.join(','),
            ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
        ].join('\n');

        const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `directorio_encuestas_${selectedEntity}_${selectedSection}_${new Date().toISOString().split('T')[0]}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const handleRowClick = (record) => {
        setSelectedRecord(record);
        setIsModalVisible(true);
    };

    const desktopColumns = [
        // {
        //     title: "ID Afiliación",
        //     dataIndex: "affiliationId",
        //     key: "affiliationId",
        // },
        {
            title: "Nombre",
            key: "name",
            render: (_, record) =>
                `${record.name || ''} ${record.firstLastName || ''} ${record.secondLastName || ''}`.trim(),
        },
        // {
        //     title: "Clave Elector",
        //     dataIndex: "electorKey",
        //     key: "electorKey",
        // },
        // {
        //     title: "CURP",
        //     dataIndex: "curp",
        //     key: "curp",
        // },
    ];

    const mobileColumns = [
        // {
        //     title: "ID Afiliación",
        //     dataIndex: "affiliationId",
        //     key: "affiliationId",
        // },
        {
            title: "Nombre",
            key: "name",
            render: (_, record) =>
                `${record.name || ''} ${record.firstLastName || ''}`.trim(),
        },
        // {
        //     title: "Clave Elector",
        //     dataIndex: "electorKey",
        //     key: "electorKey",
        // }
    ];

    return (
        <>
            <motion.div
                initial={{opacity: 0, y: -10}}
                animate={{opacity: 1, y: 0}}
                transition={{duration: 0.5}}
                style={{
                    maxWidth: "90%",
                    width: "90vw",
                    margin: "20px auto",
                    background: "#fff",
                    padding: "20px",
                    borderRadius: "8px",
                    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
                    overflowX: "auto",
                }}
            >
                <Title level={2} style={{textAlign: "center"}}>
                    Directorio
                </Title>

                {[COT_ROLE, SECTIONAL_ROLE, PROMOTER_ROLE].includes(userRole) && data.length > 0 && (
                    <Row style={{marginBottom: "20px"}}>
                        <Col span={24} style={{textAlign: "right"}}>
                            <Button 
                                type="primary" 
                                icon={<DownloadOutlined/>}
                                onClick={downloadCSV}
                            >
                                Descargar CSV
                            </Button>
                        </Col>
                    </Row>
                )}

                <Form form={form} layout="vertical" style={{marginBottom: "20px"}}>
                    <Row gutter={[16, 16]}>
                        <Col xs={24} md={12}>
                            <Form.Item name="entityId" label="Entidad" rules={[{ required: true, message: 'Selecciona una entidad' }]}>
                                <Select
                                    placeholder="Selecciona una entidad"
                                    onChange={(value) => setSelectedEntity(value)}
                                >
                                    {userEntities.map((id) => (
                                        <Option key={id} value={id}>
                                            {`Entidad ${id}`}
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={12}>
                            <Form.Item name="sectionId" label="Sección" rules={[{ required: true, message: 'Selecciona una sección' }]}>
                                <Select
                                    placeholder="Selecciona una sección"
                                    onChange={(value) => setSelectedSection(value)}
                                    showSearch
                                    filterOption={(input, option) =>
                                        option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                                    }
                                >
                                    {userSections.map((sec) => (
                                        <Option key={sec} value={sec}>
                                            {`Sección ${sec}`}
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row>
                        <Col span={24} style={{textAlign: "right"}}>
                            <Space>
                                <Button type="primary" onClick={handleSearch} icon={<SearchOutlined/>}>
                                    Buscar
                                </Button>
                                <Button onClick={handleReset} icon={<UndoOutlined/>}>
                                    Limpiar
                                </Button>
                            </Space>
                        </Col>
                    </Row>
                </Form>

                <Table
                    columns={isMobile ? mobileColumns : desktopColumns}
                    dataSource={data}
                    rowKey={(record) => record.electorKey || record.affiliationId}
                    loading={loading}
                    pagination={{
                        ...pagination,
                        showSizeChanger: true,
                        pageSizeOptions: ["10", "20", "50"],
                    }}
                    onChange={handleTableChange}
                    onRow={(record) => ({
                        onClick: () => {
                            handleRowClick(record);
                        },
                        style: {cursor: "pointer"},
                    })}
                />

                <Modal
                    title="Detalles del registro"
                    open={isModalVisible}
                    onCancel={() => setIsModalVisible(false)}
                    footer={null}
                    width={800}
                >
                    {selectedRecord && (
                        <>
                            <Descriptions bordered column={1} size="small">
                                <Descriptions.Item label="Nombre completo">
                                    {`${selectedRecord.name || ''} ${selectedRecord.firstLastName || ''} ${selectedRecord.secondLastName || ''}`.trim()}
                                </Descriptions.Item>
                                {/* <Descriptions.Item label="Clave de elector">{selectedRecord.electorKey}</Descriptions.Item> */}
                                {/* <Descriptions.Item label="CURP">{selectedRecord.curp}</Descriptions.Item> */}
                                <Descriptions.Item label="Teléfono">{selectedRecord.phone}</Descriptions.Item>
                                <Descriptions.Item label="Email">{selectedRecord.email || 'N/A'}</Descriptions.Item>
                                <Descriptions.Item label="Sección">{selectedRecord.section}</Descriptions.Item>
                                <Descriptions.Item label="Entidad">{selectedRecord.entityId}</Descriptions.Item>
                                <Descriptions.Item label="Municipio">{selectedRecord.municipality || 'N/A'}</Descriptions.Item>
                                <Descriptions.Item label="Distrito">{selectedRecord.district || 'N/A'}</Descriptions.Item>
                                <Descriptions.Item label="Dirección INE">{selectedRecord.ineAddress || 'N/A'}</Descriptions.Item>
                                {/* <Descriptions.Item label="ID Afiliación">{selectedRecord.affiliationId}</Descriptions.Item> */}
                                <Descriptions.Item label="Formación">{selectedRecord.formation ? 'Sí' : 'No'}</Descriptions.Item>
                                <Descriptions.Item label="Comité">{selectedRecord.committee ? 'Sí' : 'No'}</Descriptions.Item>
                                <Descriptions.Item label="Promotor">{selectedRecord.promoter ? 'Sí' : 'No'}</Descriptions.Item>
                                <Descriptions.Item label="RC">{selectedRecord.rc ? 'Sí' : 'No'}</Descriptions.Item>
                                <Descriptions.Item label="Participación">
                                    {selectedRecord.participationArray ? selectedRecord.participationArray.join(', ') : 'N/A'}
                                </Descriptions.Item>
                                <Descriptions.Item label="Coordenadas">{selectedRecord.latLong || 'N/A'}</Descriptions.Item>
                            </Descriptions>
                            {selectedRecord.latLong && (() => {
                                const [lat, lng] = selectedRecord.latLong.split(',').map(coord => parseFloat(coord.trim()));
                                if (!isNaN(lat) && !isNaN(lng)) {
                                    return (
                                        <div style={{ marginTop: '20px', height: '300px', width: '100%', position: 'relative' }}>
                                            <MapContainer
                                                key={mapKey}
                                                center={[lat, lng]}
                                                zoom={15}
                                                style={{ height: '100%', width: '100%', zIndex: 0 }}
                                                scrollWheelZoom={false}
                                            >
                                                <TileLayer
                                                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                                                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                                                />
                                                <Marker position={[lat, lng]}>
                                                    <Popup>
                                                        {`${selectedRecord.name || ''} ${selectedRecord.firstLastName || ''}`.trim()}
                                                    </Popup>
                                                </Marker>
                                            </MapContainer>
                                        </div>
                                    );
                                }
                                return null;
                            })()}
                        </>
                    )}
                </Modal>
            </motion.div>
        </>
    );
};

export default DirectorySurveyPage;
